import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AIChadGreeting from '@/components/ai-chad/AIChadGreeting';
import { bandSentence } from '@/components/dashboard/mastheadCopy';
import '@/styles/mydesk-surface.css';
import { resolveMasthead, DEFAULT_ANCHOR, DEFAULT_PLATE_STRENGTH } from '@/utils/mastheadImage';
import '@/styles/km-mast.css';
import { getCompanySettings, peekCompanySettings, subscribeCompanySettings }
  from '@/utils/companySettings';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DESIGN PASS 18 — the masthead band, lifted out of the desk surface.
 * It carries what it carried: the greeting, the weather and time, the four
 * figures, Customize, Warning, the quote.
 *
 * DESIGN PASS 19 — the band's background is the TENANT's choice, not the
 * user's: `masthead_background` is 'planes' (default), 'flat', or 'image'.
 * The image is one upload with two persistent crops — wide for desktop,
 * narrow for the phone — and a scrim that sits behind the text only.
 */
/*
 * DP82 Part 4 — REUSED BY /projects, not copied.
 * Four optional props were added so a second page can use this exact band —
 * the same CSS in mydesk-surface.css, the same planes/flat/image background
 * read from the SAME `masthead_background` company setting (no third field),
 * the same scrim mechanism, the same figure treatment, the same quote block.
 * `sentence` overrides the copy, `subline` adds the small-caps line /projects
 * needs, `greeting` replaces the AI Chad column (on /projects Chad moved into
 * card A.2), and `testId` names the band. With none of them passed My Desk
 * behaves exactly as before.
 */
export default function MastheadBand({ user, todayTasks, events, darkMode, figures, onCustomizeClick,
                                       sentence: sentenceOverride, subline, greeting, testId,
                                       page = 'mydesk' }) {
  // DP98 Part 1 — 'pending', not 'planes'. The band drew the DEFAULT while the
  // settings request was in flight, so every gateway flashed planes before the
  // firm's own background arrived. Until the answer is here it commits to
  // nothing: no planes, no picture, no colour, just the band's own surface. If
  // the session already has the answer there is no pending state at all.
  const [background, setBackground] = useState(
    () => (peekCompanySettings() ? resolveMasthead(peekCompanySettings(), page).kind : 'pending'),
  );
  const [image, setImage] = useState(null);
  const [flatColour, setFlatColour] = useState(null);
  // DP100 Part 4 — the plate, the type colour and the position, per page.
  const [plate, setPlate] = useState({
    colour: null, strength: DEFAULT_PLATE_STRENGTH, ink: null, anchor: DEFAULT_ANCHOR,
  });
  const [cropWide, setCropWide] = useState(null);
  const [cropNarrow, setCropNarrow] = useState(null);
  const [scrim, setScrim] = useState('standard');
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    // Below 768px the phone crop is used; at 768 and above the desktop crop is
    // used and simply scales, so nothing re-crops at intermediate widths.
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // DP97 Part 1 — the KIND is per page now, not one tenant field, so a picture
    // on the Work Table cannot put a picture on the other two.
    // DP98 Part 1 — and the answer is held for the session, so moving between the
    // three gateways asks once, not three times.
    const apply = (d) => {
      if (cancelled || !d) return;
      const m = resolveMasthead(d, page);
      setBackground(m.kind);
      setImage(m.image);
      setFlatColour(m.colour);
      setPlate({
        colour: m.plateColour, strength: m.plateStrength,
        ink: m.typeColour, anchor: m.anchor,
      });
      setCropWide(m.cropWide);
      setCropNarrow(m.cropNarrow);
      setScrim(d.masthead_image_scrim === false ? 'off' : (d.masthead_image_scrim_strength || 'standard'));
    };
    apply(peekCompanySettings());
    getCompanySettings().then(apply).catch(() => {});
    const off = subscribeCompanySettings(apply);
    return () => { cancelled = true; off(); };
  }, [page]);

  // MH-1 — the band keeps the last picture it actually has while a new one
  // loads, and a picture that will not load is not a broken mark: one retry
  // after two seconds, then the plate colour, as if there were no picture.
  const [shownUrl, setShownUrl] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [imageDown, setImageDown] = useState(false);
  const retryTimer = useRef(null);
  const nextUrl = image?.url || null;
  useEffect(() => {
    setAttempt(0);
    setImageDown(false);
  }, [nextUrl]);
  useEffect(() => () => clearTimeout(retryTimer.current), []);
  // The masthead is the biggest thing on the page and the desk asks for it
  // last: on My Desk the browser is told to start it before the payloads.
  useEffect(() => {
    if (page !== 'mydesk' || !nextUrl) return undefined;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = nextUrl;
    document.head.appendChild(link);
    return () => link.remove();
  }, [page, nextUrl]);

  const crop = (narrow ? cropNarrow : cropWide) || { x: 0, y: 0, w: 1, h: 1 };
  const showImage = background === 'image' && shownUrl;
  // The band states a fact: how many projects are moving. Never advice.
  const sentence = sentenceOverride
    || bandSentence((figures.find((f) => f.label === 'Active') || {}).value);
  // DP35 — the quotes are the firm's. Random per load, as ruled. With none
  // set the band shows nothing and does not collapse.
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenant-quotes`)
      .then(({ data }) => {
        const list = data.quotes || [];
        setQuote(list.length ? list[Math.floor(Math.random() * list.length)] : null);
      })
      .catch(() => setQuote(null));
  }, []);

  const plateOn = showImage && plate.strength > 0;
  const bandStyle = {
    ...(background === 'flat' && flatColour ? { background: flatColour } : null),
    ...(plate.colour ? { '--km-plate': plate.colour } : null),
    ...(plate.ink ? { '--km-mast-ink': plate.ink } : null),
    '--km-plate-a': plate.strength / 100,
  };

  return (
    <div className="kmd-masthead km-mast" data-bg={background}
         style={bandStyle}
         data-plate={plateOn ? 'on' : 'off'}
         data-plate-strength={plate.strength}
         data-anchor={plate.anchor}
         data-typecolour={plate.ink ? 'own' : 'token'}
         data-scrim={showImage ? scrim : 'off'} data-testid={testId || 'mydesk-masthead'}>
      {background === 'planes' && (
        <div className="kmd-planes" aria-hidden="true" data-testid="masthead-planes">
          <span /><span /><span />
        </div>
      )}
      {/* MH-1 — the loader: out of the layout and never seen, so a failed
          picture cannot draw a broken mark over the band. */}
      {background === 'image' && nextUrl && nextUrl !== shownUrl && !imageDown && (
        <img
          key={`${nextUrl}#${attempt}`}
          src={nextUrl}
          alt=""
          aria-hidden="true"
          style={{ display: 'none' }}
          data-testid="masthead-image-loader"
          onLoad={() => setShownUrl(nextUrl)}
          onError={() => {
            if (attempt === 0) {
              retryTimer.current = setTimeout(() => setAttempt(1), 2000);
            } else {
              setImageDown(true);
            }
          }}
        />
      )}
      {showImage && (
        <div className="kmd-mastimg" aria-hidden="true" data-testid="masthead-image-layer">
          <img
            src={shownUrl}
            alt=""
            data-testid="masthead-image"
            style={{
              width: `${100 / crop.w}%`,
              height: `${100 / crop.h}%`,
              left: `${(-crop.x / crop.w) * 100}%`,
              top: `${(-crop.y / crop.h) * 100}%`,
            }}
          />
        </div>
      )}
      {/* DP100 Part 4 — ONE GROUP. Everything the band draws sits in it, the
          quote included, so the nine positions move all of it together. */}
      <div className="km-mast-group" data-testid="masthead-group">
      <div className="kmd-masthead__head" data-testid="masthead-head">
        <h1 className="kmd-mast-sentence" data-testid="masthead-sentence">
          {sentence.pre}
          {sentence.numeral && (
            <span className="kmd-mast-sentence__n" data-testid="masthead-sentence-figure">
              {sentence.numeral}
            </span>
          )}
          {sentence.mid}
          {sentence.numeral2 && (
            <span className="kmd-mast-sentence__n" data-testid="masthead-sentence-figure-2">
              {sentence.numeral2}
            </span>
          )}
          {sentence.post}
        </h1>
        {subline && (
          <p className="kmd-mast-subline" data-testid="masthead-subline">{subline}</p>
        )}
        {/* DP32 §3 — the tagline is REMOVED from the masthead. "Let's Make a
            Plan" is the firm's slogan and belongs beside the logo in the left
            column, where it still sits untouched (TenantTagline, rendered by
            FixedBottomNav). It is not a page heading. */}
      </div>
      <div className="kmd-masthead__greeting">
        {greeting !== undefined ? greeting : <AIChadGreeting
          hideChat={true}
          hideQuote={true}
          userName={user.name}
          screen="mydesk"
          todayTasks={todayTasks}
          calendarEvents={events}
          darkMode={darkMode}
          userId={user.id}
          tallies={[]}
          onCustomizeClick={onCustomizeClick}
        />}
      </div>
      <div className="kmd-masthead__figures">
        {figures.map((f) => (
          <div className="kmd-mfig" key={f.label} data-testid={`masthead-figure-${f.label.toLowerCase()}`}>
            <span className="kmd-mfig__value" data-tone={f.tone}>{f.value}</span>
            <span className="kmd-mfig__label">{f.label}</span>
          </div>
        ))}
      </div>
      {/* DP32 §3 — the quote: full width, centred, on the band's lower edge
          above the 1px rule. It belongs to the BAND, not to the greeting
          column, which is why it is rendered here. */}
      <div className="kmd-mast-quote" data-testid="masthead-quote">
        {quote ? (
          <p className="m-0">
            <span className="kmd-mast-quote__text">&ldquo;{quote.text}&rdquo;</span>
            {quote.author ? (
              <span className="kmd-mast-quote__attrib">&mdash; {quote.author}</span>
            ) : null}
          </p>
        ) : null}
      </div>
      </div>
    </div>
  );
}
